import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';
import '../styles/pages/_quiz.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "According to the IPAQ-SF framework, which activity intensity level is best for reducing stiffness and aiding recovery?",
    options: [
      "Vigorous intensity activities like running",
      "Moderate intensity activities like brisk walking",
      "Light intensity activities like gentle yoga",
      "All intensity levels provide the same benefits"
    ],
    correctAnswer: 2,
    explanation: "Light intensity activities like gentle yoga and stretching are specifically mentioned as beneficial for reducing stiffness and aiding recovery, though all levels of activity provide health benefits."
  },
  {
    question: "The '20-20 Move Rule' suggests:",
    options: [
      "Moving for 20 minutes every 20 hours",
      "Moving for 20 seconds every 20 minutes of sitting",
      "Walking 20,000 steps every day",
      "Doing 20 different exercises daily"
    ],
    correctAnswer: 1,
    explanation: "The 20-20 Move Rule is a practical strategy for breaking up prolonged sitting: move for 20 seconds every 20 minutes of sitting to maintain circulation and energy."
  },
  {
    question: "Which of the following is NOT mentioned as a benefit of regular movement?",
    options: [
      "Improved heart and lung function",
      "Boosted focus and mood",
      "Guaranteed weight loss",
      "Reduced risk of chronic illness"
    ],
    correctAnswer: 2,
    explanation: "While movement supports weight management, it's not a guarantee of weight loss. The other options (heart health, mood, and chronic illness prevention) are all established benefits of regular physical activity."
  },
  {
    question: "If someone sits more than 8 hours per day, what is their status according to the sitting guidelines?",
    options: [
      "Active (maintain balance)",
      "Moderate risk (add stretch breaks)",
      "Sedentary (move hourly)",
      "Low risk (no changes needed)"
    ],
    correctAnswer: 2,
    explanation: "Sitting more than 8 hours per day is classified as sedentary, requiring more active intervention like moving every hour to counteract the negative effects of prolonged sitting."
  },
  {
    question: "Which activity is recommended as a way to stay active daily?",
    options: [
      "Only going to the gym",
      "Taking stairs instead of elevators",
      "Watching exercise videos without moving",
      "Sitting more comfortably"
    ],
    correctAnswer: 1,
    explanation: "Taking stairs instead of elevators is specifically mentioned as an easy way to incorporate more movement into daily life, along with other practical suggestions like walking while on phone calls."
  }
];

const PhysicalModule1QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('physicalM1');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  React.useEffect(() => {
    if (responses[currentQuestion] !== undefined) {
      setSelectedAnswer(responses[currentQuestion]);
      setShowExplanation(true);
    } else {
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  }, [currentQuestion, responses]);

  const handleAnswerSelect = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
    setShowExplanation(true);
    setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const correctAnswers = Object.values(responses).filter((answer, index) =>
    answer === quizQuestions[index].correctAnswer
  ).length;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 1 quiz!
        </p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check"></span>
                <span>The foundations of physical wellness</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Personal movement and energy strategies</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Consistency in healthy habits</span>
              </li>
            </ul>
          </div>
          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>Continue your physical wellness journey by staying active and balanced!</p>
            <p>Ready to deepen your understanding of movement and energy?</p>
          </div>
          <button
            className="resource-button"
            onClick={() => navigate('/panel/learn/physical-resources')}
          >
            Return to Physical Resources
          </button>
        </div>
      </div>
    );
  }

  const currentQuizQuestion = quizQuestions[currentQuestion];

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left physical-panel">
          <div className="module-header">
            <h1>Module 1 Quiz</h1>
            <p className="module-subtitle">Understanding Physical Wellness</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned in a stress-free way.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>
            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>
              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? () => navigate('/panel/learn/physical-resources') : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={responses[currentQuestion] === undefined}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={responses[currentQuestion] === undefined}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhysicalModule1QuizPage;

